import { useState } from "react";
import { Loader2, MessageSquareText, ScrollText, Send, X } from "lucide-react";
import { errorMessage } from "../../hooks/useCommission";
import { fmtDate } from "../proposal/format";

// Route A, dark section: the special conditions agreed for this project.
export function SpecialConditionsNotice({ conditions }) {
  if (!conditions?.text) return null;
  return (
    <div className="flex flex-col gap-3 rounded-lg border border-emerald-400/40 bg-emerald-400/10 p-5 text-emerald-50" data-testid="sow-special-conditions">
      <p className="flex items-center gap-2 font-semibold"><ScrollText className="w-4 h-4 text-emerald-300" /> Special conditions agreed for this project</p>
      <p className="text-sm whitespace-pre-line leading-relaxed text-emerald-50/90" data-testid="sow-special-conditions-text">{conditions.text}</p>
      <p className="text-xs text-emerald-100/70">
        Recorded by {conditions.set_by_name || "Medicys"} on {fmtDate(conditions.set_at)}. They appear as section 6 of the Statement of Work and prevail over the Terms and Conditions of Business where they conflict.
      </p>
    </div>
  );
}

// Route A: ask Medicys for a change to the document or the Terms before signing.
export function ChangeRequestBox({ requests = [], onRequest, canWrite }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const pending = requests.find((r) => r.status === "open");
  const closed = [...requests].reverse().find((r) => r.status === "resolved" && r.resolution === "no_change");
  const ok = text.trim().length >= 10;

  const submit = async () => {
    setSending(true);
    setError(null);
    try {
      await onRequest(text.trim());
      setText("");
      setOpen(false);
    } catch (e) {
      setError(errorMessage(e, "We could not send your request. Please try again."));
    } finally {
      setSending(false);
    }
  };

  if (pending) {
    return (
      <div className="flex flex-col gap-2 rounded-lg border border-[#3EA8DC]/40 bg-[#3EA8DC]/10 px-4 py-3 text-sm text-stone-200" data-testid="sow-change-request-pending">
        <p className="flex items-center gap-2 font-semibold text-[#9FD8F2]"><MessageSquareText className="w-4 h-4" /> Change requested on {fmtDate(pending.requested_at)}</p>
        <p className="whitespace-pre-line text-stone-300" data-testid="sow-change-request-pending-text">{pending.text}</p>
        <p className="text-xs text-stone-400">Medicys has been notified and will come back to you by email. If the change is agreed it is added to the Statement of Work as special conditions and you will be asked to review and sign. You can still sign the document as it stands if you prefer.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 rounded-lg border border-white/10 px-4 py-3 text-sm text-stone-300" data-testid="sow-change-request">
      {open ? (
        <>
          <label className="flex flex-col gap-1.5">
            <span className="font-semibold text-stone-100">What would you like changed, and why?</span>
            <span className="text-xs text-stone-400">Quote the clause or section if you can. Your Medicys contact will reply by email; agreed changes are recorded as special conditions in the Statement of Work, so nothing needs to be edited by hand.</span>
            <textarea value={text} onChange={(e) => setText(e.target.value)} rows={4} placeholder="e.g. Our procurement team would like the indemnity in clause 18 to be mutual."
              className="mt-1 rounded-md border border-white/15 bg-[#0B1220] px-3 py-2 text-sm text-stone-100 placeholder:text-stone-500 focus:outline-none focus:border-[#3EA8DC]" data-testid="sow-change-request-text" />
          </label>
          {error && <p className="text-sm text-rose-300" data-testid="sow-change-request-error">{error}</p>}
          <div className="flex flex-wrap items-center gap-3">
            <button type="button" onClick={submit} disabled={sending || !ok} className="inline-flex items-center gap-2 rounded-full bg-[#3EA8DC] px-5 py-2.5 text-sm font-semibold text-[#0B1220] hover:bg-[#5FBCE6] disabled:opacity-40 transition-colors" data-testid="sow-change-request-submit">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send the request to Medicys
            </button>
            <button type="button" onClick={() => setOpen(false)} className="inline-flex items-center gap-1.5 text-sm text-stone-400 hover:text-stone-200" data-testid="sow-change-request-cancel"><X className="w-3.5 h-3.5" /> Cancel</button>
            {!ok && text.length > 0 && <span className="text-xs text-stone-500">A little more detail is needed.</span>}
          </div>
        </>
      ) : (
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <span className="flex-1">
            Does your legal or procurement team need a change to the Statement of Work or to our Terms before signing? Ask here and we will agree the wording with you; there is no need to switch to your own contract.
            {closed ? ` Your request of ${fmtDate(closed.requested_at)} was answered by email; the document is unchanged.` : ""}
          </span>
          <button type="button" onClick={() => setOpen(true)} disabled={!canWrite} className="self-start sm:self-auto shrink-0 inline-flex items-center gap-2 rounded-full border border-[#3EA8DC]/70 px-4 py-2 font-semibold text-[#9FD8F2] hover:bg-[#3EA8DC]/10 transition-colors disabled:opacity-50" data-testid="sow-change-request-open">
            <MessageSquareText className="w-4 h-4" /> Request a change
          </button>
        </div>
      )}
    </div>
  );
}
